import React from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileAlt, FaUniversity } from "react-icons/fa";

const CorporateSleek = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-slate-900 min-h-[1100px] flex overflow-hidden font-sans">
            {/* Dark Sidebar */}
            <div className="w-[30%] p-12 text-slate-400 flex flex-col justify-between">
                <div className="space-y-12">
                    <div className="space-y-6">
                        {companyDetails?.logo ? (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto object-contain brightness-0 invert" />
                        ) : (
                            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white text-2xl font-black border border-white/5 shadow-2xl">
                                {companyDetails?.name?.charAt(0)}
                            </div>
                        )}
                        <div className="space-y-1">
                            <h2 className="text-white font-black text-xl tracking-tighter uppercase italic">{companyDetails?.name}</h2>
                            <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-slate-600 leading-relaxed">{companyDetails?.address}</p>
                        </div>
                    </div>

                    <div className="space-y-10">
                        <section className="space-y-4">
                            <h4 className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 border-b border-white/5 pb-2">Communications</h4>
                            <div className="space-y-3 text-[11px] font-bold text-slate-400">
                                <div className="flex items-center gap-3 transition-colors hover:text-white cursor-default"><FaPhone className="text-slate-600" /> {companyDetails?.phone}</div>
                                <div className="flex items-center gap-3 transition-colors hover:text-white cursor-default"><FaEnvelope className="text-slate-600" /> {companyDetails?.email}</div>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h4 className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 border-b border-white/5 pb-2">Settlement Account</h4>
                            <div className="space-y-2 text-[11px] font-bold text-slate-400">
                                <div className="text-slate-500 uppercase italic tracking-tight">{companyDetails?.bankName}</div>
                                <div className="text-white font-mono tracking-tighter truncate">ACC: {companyDetails?.accountNo}</div>
                            </div>
                        </section>
                    </div>
                </div>

                <div className="space-y-4 pt-10 border-t border-white/5 opacity-40">
                    <div className="text-[9px] font-black uppercase tracking-[0.5em] text-slate-400 items-center flex gap-2">
                        Official Business Record
                    </div>
                    <p className="text-[9px] leading-relaxed text-slate-600 font-medium italic">
                        {terms || "Precision accounting and financial clarity for modern business operations. Secure settlement document."}
                    </p>
                </div>
            </div>

            {/* Light Main Content */}
            <div className="flex-1 bg-white rounded-l-[4rem] p-16 flex flex-col justify-between shadow-[-50px_0_100px_rgba(0,0,0,0.3)] border-l border-white/10">
                {/* Header */}
                <div className="flex justify-between items-start border-b-2 border-slate-900 pb-10 mb-12">
                    <div className="space-y-4">
                        {companyDetails?.logo ? (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto object-contain" />
                        ) : (
                            <div className="text-4xl font-black tracking-tighter text-slate-900 uppercase italic">
                                {companyDetails?.name?.split(' ')[0] || "INVOICE"}
                            </div>
                        )}
                        <h1 className="text-5xl font-black uppercase tracking-tighter text-slate-900 mt-4 leading-none">INVOICE</h1>
                    </div>

                    <div className="text-right space-y-2">
                        <div className="bg-slate-900 text-white px-6 py-2 rounded-lg inline-block font-black text-sm uppercase tracking-widest mb-4">
                            # {title || "INV-001"}
                        </div>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                            <div className="text-left">Date Issued:</div>
                            <div className="text-slate-900">{date}</div>
                            <div className="text-left">Due Date:</div>
                            <div className="text-slate-900">{dueDate}</div>
                        </div>
                    </div>
                </div>

                {/* Addresses */}
                <div className="grid grid-cols-2 gap-20 mb-12 px-2">
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 border-l-2 border-slate-900 pl-3">Company Details</h4>
                        <div className="space-y-1">
                            <div className="text-lg font-black text-slate-900">{companyDetails?.name}</div>
                            <div className="text-sm font-medium text-slate-500 max-w-[250px]">{companyDetails?.address}</div>
                            <div className="flex flex-col pt-3 space-y-2 text-xs font-bold text-slate-400">
                                <span className="flex items-center gap-2 text-slate-600"><FaPhone className="text-slate-400" /> {companyDetails?.phone}</span>
                                <span className="flex items-center gap-2 text-slate-600"><FaEnvelope className="text-slate-400" /> {companyDetails?.email}</span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 border-l-2 border-slate-900 pl-3">Bill To</h4>
                        <div className="space-y-1">
                            <div className="text-lg font-black text-slate-900">{to}</div>
                            <div className="text-sm font-medium text-slate-500 max-w-[250px]">{clientAddress}</div>
                            <div className="flex flex-col pt-3 space-y-2 text-xs font-bold text-slate-400">
                                {clientPhone && <span className="flex items-center gap-2 text-slate-600"><FaPhone className="text-slate-400" /> {clientPhone}</span>}
                                {clientEmail && <span className="flex items-center gap-2 text-slate-600"><FaEnvelope className="text-slate-400" /> {clientEmail}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-12">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-y border-slate-200 text-slate-900 font-black uppercase text-[10px] tracking-widest">
                                <th className="p-6">Description</th>
                                <th className="p-6 text-center">Qty</th>
                                <th className="p-6 text-right">Unit Price</th>
                                <th className="p-6 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 border-b border-slate-100">
                            {(invoiceItems || []).map((item, idx) => (
                                <tr key={idx} className="group hover:bg-slate-50 transition-colors">
                                    <td className="p-8">
                                        <div className="font-black text-slate-900 uppercase tracking-tight text-lg italic">{item.item}</div>
                                        <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1 italic">{item.description || "Professional Service"}</div>
                                    </td>
                                    <td className="p-8 text-center font-black text-slate-500 italic">{item.qty}</td>
                                    <td className="p-8 text-right font-black text-slate-500 italic">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-8 text-right font-black text-slate-900 text-xl tracking-tighter italic leading-none">
                                        Nu. {parseFloat(item.amount).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals & Notes Row */}
                <div className="grid grid-cols-2 gap-12 px-2">
                    <div className="space-y-6">
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 italic text-slate-500 text-sm leading-relaxed border-l-4 border-slate-900">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 not-italic mb-2">Important Notes</h4>
                            {notes || "Thank you for your business. We appreciate your prompt payment and professional partnership."}
                        </div>
                        {terms && (
                            <div className="px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Terms: {terms}
                            </div>
                        )}
                    </div>

                    <div className="bg-slate-900 text-white p-10 rounded-2xl shadow-2xl relative overflow-hidden border border-slate-800">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                        <div className="space-y-4 relative z-10 w-full">
                            <div className="flex justify-between text-xs font-bold text-slate-500 uppercase tracking-widest">
                                <span>Subtotal</span>
                                <span className="text-white">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold text-slate-500 uppercase tracking-widest">
                                <span>Tax ({taxRate}%)</span>
                                <span className="text-white">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-widest">
                                    <span>Discount</span>
                                    <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                                </div>
                            )}
                            <div className="pt-8 mt-8 border-t border-white/10 flex justify-between items-end">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Total Payable</span>
                                    <div className="text-5xl font-black tracking-tighter italic">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-20 border-t border-slate-100 pt-10 flex justify-between items-end px-2">
                <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 border-l-2 border-slate-900 pl-3">Banking Details</h4>
                    <div className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl border border-slate-100">
                        <FaUniversity className="text-slate-400 mt-1" />
                        <div className="text-xs space-y-1 font-bold">
                            <div className="text-slate-900 uppercase tracking-tight italic">{companyDetails?.bankName || "Digital Kidu Bank"}</div>
                            <div className="text-slate-500 font-mono tracking-tighter uppercase whitespace-nowrap overflow-hidden text-ellipsis">ACC NO: {companyDetails?.accountNo || "122656705"}</div>
                        </div>
                    </div>
                </div>

                <div className="text-right space-y-6">
                    <div className="w-56 h-px bg-slate-200 ml-auto"></div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-300 italic">Signature / Stamp</div>
                </div>
            </div>
        </div>
    );
};

export default CorporateSleek;
